/**
 * Penilaian Periods management — year view, 4 quarters per year.
 * HR can create all 4 quarters at once (including future dates) or per quarter.
 */

import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import type { AssessmentPeriod, CreatePeriodInput, PeriodStatus } from '../types/penilaian.types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<PeriodStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  open: 'bg-green-100 text-green-700',
  closed: 'bg-red-100 text-red-700',
}

const STATUS_LABELS: Record<PeriodStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  closed: 'Closed',
}

const QUARTER_LABELS: Record<number, string> = {
  1: 'Triwulan I',
  2: 'Triwulan II',
  3: 'Triwulan III',
  4: 'Triwulan IV',
}

const QUARTER_MONTH_LABELS: Record<number, string> = {
  1: 'Jan – Mar',
  2: 'Apr – Jun',
  3: 'Jul – Sep',
  4: 'Okt – Des',
}

function defaultDates(year: number, quarter: number) {
  const ranges: Record<number, { start: string; end: string }> = {
    1: { start: `${year}-01-01`, end: `${year}-03-31` },
    2: { start: `${year}-04-01`, end: `${year}-06-30` },
    3: { start: `${year}-07-01`, end: `${year}-09-30` },
    4: { start: `${year}-10-01`, end: `${year}-12-31` },
  }
  return ranges[quarter]
}

// ─── Quarter card ──────────────────────────────────────────────────────────────

interface QuarterCardProps {
  quarter: number
  year: number
  period?: AssessmentPeriod
  onOpen: (id: string) => void
  onClose: (id: string) => void
  onCreate: (data: CreatePeriodInput) => void
  isPending: boolean
}

function QuarterCard({ quarter, year, period, onOpen, onClose, onCreate, isPending }: QuarterCardProps) {
  const defaults = defaultDates(year, quarter)
  const [form, setForm] = useState({
    start_date: defaults.start,
    end_date: defaults.end,
    submission_deadline: '',
  })
  const [expanded, setExpanded] = useState(false)

  if (period) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                Q{quarter}
              </span>
              <span className="font-semibold text-gray-900">{QUARTER_LABELS[quarter]}</span>
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{QUARTER_MONTH_LABELS[quarter]} {year}</div>
          </div>
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${STATUS_STYLES[period.status]}`}>
            {STATUS_LABELS[period.status]}
          </span>
        </div>

        <div className="text-xs text-gray-600 space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-400">Mulai</span>
            <span>{period.start_date}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Selesai</span>
            <span>{period.end_date}</span>
          </div>
          {period.submission_deadline && (
            <div className="flex justify-between">
              <span className="text-gray-400">Deadline</span>
              <span>{period.submission_deadline}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
          <Link
            to={`/penilaian/periods/${period.id}`}
            className="text-xs text-blue-600 hover:underline font-medium"
          >
            Detail →
          </Link>
          {period.status === 'draft' && (
            <button
              onClick={() => onOpen(period.id)}
              disabled={isPending}
              className="text-xs text-green-600 hover:underline font-medium disabled:opacity-50"
            >
              Buka
            </button>
          )}
          {period.status === 'open' && (
            <button
              onClick={() => onClose(period.id)}
              disabled={isPending}
              className="text-xs text-red-600 hover:underline font-medium disabled:opacity-50"
            >
              Tutup
            </button>
          )}
        </div>
      </div>
    )
  }

  // No period yet
  return (
    <div className="bg-white border border-dashed border-gray-300 rounded-xl p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
              Q{quarter}
            </span>
            <span className="font-semibold text-gray-500">{QUARTER_LABELS[quarter]}</span>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">{QUARTER_MONTH_LABELS[quarter]} {year}</div>
        </div>
        <span className="text-xs text-gray-300 font-medium">Belum dibuat</span>
      </div>

      {!expanded ? (
        <button
          onClick={() => setExpanded(true)}
          className="w-full py-2 border border-gray-200 text-gray-500 hover:border-blue-400 hover:text-blue-600 rounded-lg text-sm font-medium transition-colors"
        >
          + Buat Periode
        </button>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Tanggal Mulai</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Tanggal Selesai</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Deadline Submisi (opsional)</label>
            <input
              type="date"
              value={form.submission_deadline}
              onChange={(e) => setForm({ ...form, submission_deadline: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() =>
                onCreate({
                  quarter,
                  year,
                  start_date: form.start_date,
                  end_date: form.end_date,
                  submission_deadline: form.submission_deadline || null,
                })
              }
              disabled={isPending || !form.start_date || !form.end_date}
              className="flex-1 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan…' : 'Buat'}
            </button>
            <button
              onClick={() => setExpanded(false)}
              className="px-3 py-1.5 border border-gray-200 text-gray-500 rounded-lg text-xs hover:bg-gray-50"
            >
              Batal
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PenilaianPeriodsPage() {
  const qc = useQueryClient()
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)

  const { data, isLoading } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  const allPeriods = data?.data ?? []

  // derive available years + always include current and next year
  const availableYears = useMemo(() => {
    const ys = new Set(allPeriods.map((p) => p.year))
    ys.add(currentYear)
    ys.add(currentYear + 1)
    return Array.from(ys).sort((a, b) => b - a)
  }, [allPeriods, currentYear])

  // periods for the selected year, keyed by quarter
  const yearMap = useMemo(() => {
    const m: Record<number, AssessmentPeriod | undefined> = {}
    allPeriods.filter((p) => p.year === selectedYear).forEach((p) => (m[p.quarter] = p))
    return m
  }, [allPeriods, selectedYear])

  const createMutation = useMutation({
    mutationFn: (d: CreatePeriodInput) => penilaianApi.createPeriod(d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penilaian-periods'] })
      toast.success('Periode berhasil dibuat')
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal membuat periode'),
  })

  const openMutation = useMutation({
    mutationFn: (id: string) => penilaianApi.updatePeriod(id, { status: 'open' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penilaian-periods'] })
      toast.success('Periode dibuka')
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal'),
  })

  const closeMutation = useMutation({
    mutationFn: (id: string) => penilaianApi.updatePeriod(id, { status: 'closed' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penilaian-periods'] })
      toast.success('Periode ditutup')
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal'),
  })

  const missingQuarters = [1, 2, 3, 4].filter((q) => !yearMap[q])

  const createAllMissing = async () => {
    for (const q of missingQuarters) {
      const dates = defaultDates(selectedYear, q)
      await createMutation.mutateAsync({
        quarter: q,
        year: selectedYear,
        start_date: dates.start,
        end_date: dates.end,
        submission_deadline: null,
      })
    }
  }

  const isPending = createMutation.isPending || openMutation.isPending || closeMutation.isPending

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Periode Penilaian</h1>
          <p className="text-sm text-gray-500 mt-1">4 periode triwulanan per tahun</p>
        </div>

        {/* Year navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSelectedYear((y) => y - 1)}
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500"
          >
            ‹
          </button>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            onClick={() => setSelectedYear((y) => y + 1)}
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500"
          >
            ›
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-20 text-gray-400">Memuat…</div>
      ) : (
        <>
          {/* 4 quarter cards */}
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((q) => (
              <QuarterCard
                key={q}
                quarter={q}
                year={selectedYear}
                period={yearMap[q]}
                onOpen={(id) => openMutation.mutate(id)}
                onClose={(id) => closeMutation.mutate(id)}
                onCreate={(d) => createMutation.mutate(d)}
                isPending={isPending}
              />
            ))}
          </div>

          {/* Bulk create all missing */}
          {missingQuarters.length > 0 && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">
                  {missingQuarters.length === 4
                    ? `Belum ada periode untuk tahun ${selectedYear}`
                    : `${missingQuarters.length} periode belum dibuat (${missingQuarters.map((q) => `Q${q}`).join(', ')})`}
                </p>
                <p className="text-xs text-blue-600 mt-0.5">
                  Buat semua sekaligus dengan tanggal default (dapat diubah setelah dibuat)
                </p>
              </div>
              <button
                onClick={createAllMissing}
                disabled={isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 whitespace-nowrap"
              >
                {isPending ? 'Membuat…' : `Buat Semua (${missingQuarters.length})`}
              </button>
            </div>
          )}

          {/* All created */}
          {missingQuarters.length === 0 && (
            <div className="mt-6 text-center text-sm text-gray-400">
              ✓ Semua 4 periode untuk tahun {selectedYear} sudah dibuat
            </div>
          )}
        </>
      )}
    </div>
  )
}
